import { styled } from '@mui/material/styles';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { semantic } from '../../../theme/tokens/semantic';
import { fontSize, fontWeight, lineHeight, radius } from '../../../theme/tokens/primitives';

/**
 * @tier atom
 *
 * Inline callout banner — a soft tinted strip for a short, passive message
 * (no icon, no dismiss). Used for the "You'll be paying …" summary under the
 * installments breakdown.
 *
 * Rich inline emphasis is supported: wrap numbers/keywords in `<strong>` and
 * they render semibold via a theme token — no manual font-weight at the call
 * site.
 *
 * Net-new primitive (no 1:1 MUI parent), so styling lives here via `styled()`
 * reading from `semantic.*` / primitive tokens — no hex, no inline `sx`.
 */
export type VyCalloutSeverity = 'info';

export interface VyCalloutProps extends HTMLAttributes<HTMLDivElement> {
  /** Visual tone. Only `info` exists today; the prop keeps the API extensible. Default: `'info'`. */
  severity?: VyCalloutSeverity;
  /** Callout body. Wrap emphasis in `<strong>` for semibold inline text. */
  children: ReactNode;
}

const CalloutRoot = styled('div')(({ theme }) => ({
  backgroundColor: semantic.callout.info.bg,
  color: semantic.callout.info.text,
  borderRadius: radius.md,
  padding: theme.spacing(1.25, 2),
  fontFamily: theme.typography.fontFamily,
  fontSize: fontSize[13],
  fontWeight: fontWeight.regular,
  lineHeight: lineHeight[20],
  '& strong': {
    fontWeight: fontWeight.semibold,
  },
}));

export const VyCallout = forwardRef<HTMLDivElement, VyCalloutProps>(
  function VyCallout({ severity = 'info', children, ...rest }, ref) {
    return (
      <CalloutRoot ref={ref} data-severity={severity} {...rest}>
        {children}
      </CalloutRoot>
    );
  },
);
